// Section 18: Work intensity policy. Hard: output 1.15x, strength -8 per season, injury 1.5x, trust -3 when chosen. Light: 0.85x, strength +5.
// Default: config.policies.defaults.workIntensity.
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import { noProblem } from '../../core/content-helpers';
import { peopleSorted } from '../../core/queries';
import { definePolicy } from '../../core/registry';

export function workIntensity(config: Config) {
  return definePolicy({
    id: 'workIntensity',
    label: 'Work intensity',
    section: 'S18',
    group: 'work',
    options: ['light', 'normal', 'hard'],
    default: config.policies.defaults.workIntensity,
    modifierTargets: ['output', 'strengthChange', 'jobRisk'],
    modifiers(ctx, target) {
      const intensity = ctx.state.policies.workIntensity;
      if (intensity === 'normal') return [];
      const c = ctx.cfg.policies;
      const source = 'policy:workIntensity';
      const hard = intensity === 'hard';
      if (target.kind === 'output')
        return [mod.mul(source, hard ? c.hardOutput : c.lightOutput, intensity)];
      if (target.kind === 'strengthChange') {
        const p = ctx.state.people[target.personId];
        if (p === undefined || p.job === 'rest') return [];
        return [mod.add(source, hard ? c.hardStrength : c.lightStrength, intensity)];
      }
      if (target.kind === 'jobRisk' && target.risk === 'injury' && hard)
        return [mod.mul(source, c.hardInjury, 'hard work')];
      return [];
    },
    changeCost: (from, to) =>
      to === 'hard' && from !== 'hard' ? config.policies.workIntensityHardTrust : 0,
    // M6: output, strength and injury as the modifiers above apply them, for the workers now.
    affects(ctx, option) {
      const c = config.policies;
      const workers = peopleSorted(ctx.state).filter((p) => p.job !== 'rest').length;
      if (option === 'normal')
        return [{ key: 'policy.workIntensity.normal', params: { workers } }];
      const hard = option === 'hard';
      return [
        {
          key: `policy.workIntensity.${option}`,
          params: {
            workers,
            output: hard ? c.hardOutput : c.lightOutput,
            strength: Math.abs(hard ? c.hardStrength : c.lightStrength),
            injury: c.hardInjury,
          },
        },
      ];
    },
    validate: noProblem,
  });
}
